import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import type { TableId } from "@/app/Common/Utils/table-id";

import {
  PaidDriverListItem,
  proDriverListQuery,
} from "@/app/ProDrivers/Drivers/GraphQL/proDriverListQuery";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { useTranslations } from "use-intl";
import {
  OrderByRelationWithColumnAggregateFunction,
  PaidDriverListQueryVariables,
  ProDriverLicenseType,
  QueryProDriversOrderByBasedInColumn,
  QueryProDriversOrderByColumn,
  QueryProDriversOrderByRelationOrderByClause,
  Region,
  SortOrder,
} from "@/gql/graphql";
import { MultiSelectFilter } from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import { SelectFilter } from "@/lib/Components/DataTable/Filters/SelectFilter";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { CityFilter } from "@/lib/Components/DataTable/Filters/CityFilter";
import { Avatar } from "@/components/catalyst/avatar";
import { getInitials } from "@/lib/Utils/getInitials";
import { Button } from "@/components/catalyst/button";
import { Checkbox } from "@/components/catalyst/checkbox";
import {
  ClipboardDocumentListIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
} from "@heroicons/react/20/solid";
import { PlusIcon } from "@heroicons/react/24/outline";
import { CopyToClipboardButton } from "@/lib/Components/Common/CopyToClipboard";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { EmailProDriverDialog } from "@/app/ProDrivers/Drivers/Components/Dialogs/EmailProDriverDialog";
import { CreateProDriverDialog } from "@/app/ProDrivers/Drivers/Components/Dialogs/CreateProDriverDialog";
import { toast } from "sonner";
import { RowSelectionState, Updater } from "@tanstack/react-table";
import {
  CellContext,
  ReactTable as Table,
} from "@/lib/Components/DataTable/types";
import { Badge } from "@/components/catalyst/badge";
import { useProDriverActions } from "@/app/ProDrivers/Drivers/Hooks/useProDriverActions";
import { useOpenProDriverDrawer } from "@/app/ProDrivers/Drivers/Components/Drawer/useProDriverDrawer";
import { ProDriverReviewDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/ProDriverReviewDialog";
import { Rating } from "@/lib/Components/Rating/Rating";
import { Highlighter } from "@/lib/Components/Highlighter";
import { ProDriverGraphic } from "@/app/ProDrivers/Drivers/Components/ProDriverGraphic";

type ProDriverColumnId =
  | "driver-info"
  | "moves"
  | "license"
  | "push-token"
  | "based-in"
  | "region"
  | "created_at"
  | "select"
  | "rating"
  | "tow-vehicle";

type TowCapableFilterValue = "true" | "false";

export type ProDriverSelection = {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
};

export function ProDriverTable({
  id,
  queryVariables,
  rightButtons,
  showActions = true,
  selection,
}: {
  id: TableId;
  queryVariables?: Partial<PaidDriverListQueryVariables>;
  rightButtons?: (table: Table<PaidDriverListItem>) => ReactNode;
  showActions?: boolean;
  selection?: ProDriverSelection;
}) {
  const getActions = useProDriverActions();
  const openDriverDrawer = useOpenProDriverDrawer();
  const { open: openCreateDialog } = useDialog(CreateProDriverDialog);
  const t = useTranslations("proDriver.table");
  const tCommon = useTranslations("common");
  const tCity = useTranslations("city");
  const tLicense = useTranslations("proDriver.licenseType");
  const tTowType = useTranslations("proDriver.towVehicleType");
  const tTow = useTranslations("proDriver.towVehicle");
  const tFilters = useTranslations("proDriver.filters");
  const [regions, setRegions] = useState<Region[]>([]);
  const [cityIds, setCityIds] = useState<string[]>([]);
  const [licenses, setLicenses] = useState<ProDriverLicenseType[]>([]);
  const [towCapable, setTowCapable] = useState<TowCapableFilterValue | null>(
    null,
  );

  const options = Object.values(Region).map((r) => ({
    label: tCity(`region.${r}`),
    value: r,
  }));

  const columns: DataTableColDef<PaidDriverListItem, any, ProDriverColumnId>[] =
    [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              table.getIsSomePageRowsSelected()
            }
            color="indigo"
            onChange={(value) => table.toggleAllPageRowsSelected(value)}
            aria-label={t("selectAll")}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            color="indigo"
            checked={row.getIsSelected()}
            onChange={(value) => row.toggleSelected(value)}
            aria-label={t("selectRow")}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "driver-info",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("driver")} />
        ),
        accessorFn: (item) => item.name,
        cell: ({ row, table }) => {
          const search = table.options.meta?.search ?? "";

          return (
            <div className="flex items-center space-x-3 py-2">
              <Avatar
                className="size-12"
                initials={getInitials(row.original.name ?? "")}
              />
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => openDriverDrawer(row.original.id)}
                  className="w-fit max-w-full truncate text-left font-medium text-gray-900 hover:text-blue-600 hover:underline"
                >
                  <Highlighter searchString={search}>
                    {row.original.name ?? ""}
                  </Highlighter>
                </button>
                <div className="mt-0.5 flex flex-col">
                  {row.original.email && (
                    <div className="flex items-center gap-1">
                      <a
                        className="truncate text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        href={`mailto:${row.original.email}`}
                      >
                        <Highlighter searchString={search}>
                          {row.original.email}
                        </Highlighter>
                      </a>
                      <CopyToClipboardButton
                        text={row.original.email}
                        className="size-4"
                      />
                    </div>
                  )}
                  {row.original.phone && (
                    <div className="flex items-center gap-1">
                      <a
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        href={`tel:${row.original.phone}`}
                      >
                        <Highlighter searchString={search}>
                          {row.original.phone}
                        </Highlighter>
                      </a>
                      <CopyToClipboardButton
                        text={row.original.phone}
                        className="size-4"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        },
        size: 300, // Set a specific width for the driver info column
      },
      {
        id: "rating",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("rating")} />
        ),
        accessorFn: (item) => item,
        cell: RatingCell,
      },
      {
        id: "moves",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("moves")} />
        ),
        accessorFn: (item) => item.numberOfCompletedAssignments,
        cell: ({ row }) => (
          <div className="text-xs text-gray-500">
            {row.original.numberOfCompletedAssignments}
          </div>
        ),
      },
      {
        id: "license",
        accessorFn: (item) => item.license_type,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("license")} />
        ),
        cell: ({ row }) => {
          if (row.original.license_type === ProDriverLicenseType.Standard) {
            return <Badge>{tLicense(ProDriverLicenseType.Standard)}</Badge>;
          }

          return (
            <Badge color="green">{tLicense(ProDriverLicenseType.Heavy)}</Badge>
          );
        },
      },
      {
        id: "tow-vehicle",
        accessorFn: (item) => item.has_tow_vehicle,
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={tTow("columnTitle")} />
        ),
        cell: ({ row }) => {
          if (!row.original.has_tow_vehicle) {
            return <div className="text-xs text-gray-400">--</div>;
          }

          return (
            <div className="text-sm">
              <Badge color="green">
                {row.original.tow_vehicle_type
                  ? tTowType(row.original.tow_vehicle_type)
                  : tTow("noDetails")}
              </Badge>
              {row.original.tow_capacity_kg ? (
                <div className="mt-1 text-xs text-gray-500">
                  {row.original.tow_vehicle_year
                    ? tTow("yearAndCapacity", {
                        year: String(row.original.tow_vehicle_year),
                        capacity: row.original.tow_capacity_kg,
                      })
                    : tTow("capacitySummary", {
                        capacity: row.original.tow_capacity_kg,
                      })}
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "push-token",
        accessorFn: (item) => item.hasPushToken,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("pushToken")} />
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <div
              className={`relative rounded-2xl ${row.original.hasPushToken ? "bg-green-400" : "bg-gray-200"} p-1`}
            >
              <DevicePhoneMobileIcon className={"size-4 text-white"} />
            </div>
          </div>
        ),
      },
      {
        id: "based-in",
        accessorFn: (item) => item.basedIn.name,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("location")} />
        ),
        cell: ({ row, table }) => {
          const search = table.options.meta?.search ?? "";

          return (
            <div className="text-sm">
              <div className="font-medium text-gray-900">
                <Highlighter searchString={search}>
                  {row.original.basedIn.name}
                </Highlighter>
              </div>
              <div className="mt-1 text-gray-500">
                {tCity(`state.${row.original.basedIn.state!}`)}
              </div>
            </div>
          );
        },
      },
      {
        id: "created_at",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("joined")} />
        ),
        accessorFn: (item) => item.created_at,
        cell: TimeSinceNowCell,
      },
    ];

  const selectedIds = selection?.selectedIds;
  const rowSelection = useMemo<RowSelectionState | undefined>(
    () =>
      selectedIds
        ? Object.fromEntries(selectedIds.map((id) => [id, true]))
        : undefined,
    [selectedIds],
  );

  return (
    <DataTable
      getActions={showActions ? getActions : undefined}
      getRowId={selection ? (row) => row.id : undefined}
      rowSelection={rowSelection}
      onRowSelectionChange={
        selection
          ? (updaterOrValue: Updater<RowSelectionState>) => {
              const next =
                typeof updaterOrValue === "function"
                  ? updaterOrValue(rowSelection ?? {})
                  : updaterOrValue;
              selection.onChange(Object.keys(next).filter((key) => next[key]));
            }
          : undefined
      }
      rightButtons={
        selection
          ? () => <ProDriverSelectionInfo selection={selection} />
          : (rightButtons ??
            ((table) => (
              <div className="flex items-center gap-2">
                <TableSelectionActionsComponent table={table} />
                <Button type="button" onClick={() => openCreateDialog({})}>
                  <PlusIcon />
                  {tCommon("add")}
                </Button>
              </div>
            )))
      }
      columns={columns}
      emptyState={
        <ProDriverEmptyState
          onAdd={selection ? undefined : () => openCreateDialog({})}
        />
      }
      document={proDriverListQuery}
      title={t("driver")}
      filters={(table) => (
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label={tFilters("region")}
            options={options}
            selected={regions}
            onChange={(newVal) => {
              table.setPagination((prev) => ({
                ...prev,
                pageIndex: 0,
              }));
              setRegions(newVal);
            }}
          />
          <CityFilter
            selected={cityIds}
            onChange={setCityIds}
            placeholder={tFilters("cities")}
            regions={regions}
          />

          <MultiSelectFilter
            label={tFilters("licenses")}
            options={[
              {
                label: tLicense(ProDriverLicenseType.Standard),
                value: ProDriverLicenseType.Standard,
              },
              {
                label: tLicense(ProDriverLicenseType.Heavy),
                value: ProDriverLicenseType.Heavy,
              },
            ]}
            selected={licenses}
            onChange={(newVal) => {
              table.setPagination((prev) => ({
                ...prev,
                pageIndex: 0,
              }));
              setLicenses(newVal);
            }}
          />

          <SelectFilter<TowCapableFilterValue>
            label={tFilters("towCapable")}
            options={[
              { label: tCommon("boolean.true"), value: "true" },
              { label: tCommon("boolean.false"), value: "false" },
            ]}
            selected={towCapable}
            onChange={(newVal) => {
              table.setPagination((prev) => ({
                ...prev,
                pageIndex: 0,
              }));
              setTowCapable(newVal);
            }}
          />
        </div>
      )}
      getQueryVariables={({ pagination, sorting, search }) => {
        if (search) {
          return {
            search,
            first: pagination.pageSize,
            page: pagination.pageIndex,
            archived: false,
            ...queryVariables,
          };
        }

        const orderBy: QueryProDriversOrderByRelationOrderByClause[] = sorting
          .map((sort) => {
            const order = sort.desc ? SortOrder.Desc : SortOrder.Asc;

            if (sort.id === "driver-info") {
              return {
                column: QueryProDriversOrderByColumn.Name,
                order,
              };
            }

            if (sort.id === "based-in") {
              return {
                basedIn: {
                  column: QueryProDriversOrderByBasedInColumn.Name,
                  aggregate: OrderByRelationWithColumnAggregateFunction.Min,
                },
                order,
              };
            }

            if (sort.id === "created_at") {
              return {
                column: QueryProDriversOrderByColumn.CreatedAt,
                order,
              };
            }

            if (sort.id === "license") {
              return {
                column: QueryProDriversOrderByColumn.LicenseType,
                order,
              };
            }

            if (
              sort.id === "rating" ||
              sort.id === "moves" ||
              sort.id === "push-token"
            ) {
              return null;
            }

            console.warn(`${sort.id} not implemented!`);

            return null;
          })
          .filter(
            Boolean,
          ) satisfies QueryProDriversOrderByRelationOrderByClause[];

        const ratingSorting = sorting.find((s) => s.id === "rating");
        const sortByRating = ratingSorting
          ? ratingSorting.desc
            ? SortOrder.Desc
            : SortOrder.Asc
          : undefined;

        const movesSorting = sorting.find((s) => s.id === "moves");
        const sortByMoves = movesSorting
          ? movesSorting.desc
            ? SortOrder.Desc
            : SortOrder.Asc
          : undefined;

        const pushTokenSorting = sorting.find((s) => s.id === "push-token");
        const sortByPushToken = pushTokenSorting
          ? pushTokenSorting.desc
            ? SortOrder.Desc
            : SortOrder.Asc
          : undefined;

        return {
          page: pagination.pageIndex,
          first: pagination.pageSize,
          region: regions.length ? regions : undefined,
          city: cityIds.length ? cityIds : undefined,
          licenseType: licenses.length ? licenses : undefined,
          hasTowVehicle:
            towCapable === null ? undefined : towCapable === "true",
          orderBy: orderBy.length ? orderBy : undefined,
          sortByRating,
          sortByMoves,
          sortByPushToken,
          archived: false,
          ...queryVariables,
        };
      }}
      accessor={(data) => data.proDrivers}
      id={id}
      initialSorting={[{ id: "created_at", desc: true }]}
    />
  );
}

function ProDriverEmptyState({ onAdd }: { onAdd?: () => void }) {
  const t = useTranslations("proDriver.empty");

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-6 text-center">
      <div className="w-full max-w-sm">
        <ProDriverGraphic />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900">{t("title")}</p>
        <p className="mt-1 text-xs text-gray-500">{t("subtitle")}</p>
      </div>
      {onAdd ? (
        <Button outline type="button" onClick={onAdd}>
          <PlusIcon />
          {t("action")}
        </Button>
      ) : null}
    </div>
  );
}

function RatingCell({ row }: CellContext<PaidDriverListItem, any>) {
  const { open } = useDialog(ProDriverReviewDialog);
  const t = useTranslations("proDriver.table");

  return (
    <div>
      <Rating
        rating={row.original.rating ?? undefined}
        onRatingChange={(rating) => {
          open({
            proDriverId: row.original.id,
            initialRating: rating,
          });
        }}
        size="sm"
      />
      {row.original.numberOfReviews ? (
        <p className="text-xs text-gray-500">
          {t("reviews", { count: row.original.numberOfReviews })}
        </p>
      ) : null}
    </div>
  );
}

function ProDriverSelectionInfo({
  selection,
}: {
  selection: ProDriverSelection;
}) {
  "use no memo";

  const t = useTranslations("proDriver.table");
  const selectedCount = selection.selectedIds.length;

  if (selectedCount === 0) {
    return null;
  }

  return (
    <div className="flex h-[36px] items-center gap-2">
      <div className="text-xs text-gray-500">
        {selectedCount} {t("selected")}
      </div>
      <Button
        plain
        type="button"
        className="text-xs text-gray-500 underline decoration-dashed"
        onClick={() => selection.onChange([])}
      >
        {t("deselect")}
      </Button>
    </div>
  );
}

interface TableSelectionActionsProps {
  table: Table<PaidDriverListItem>;
}
function TableSelectionActionsComponent({ table }: TableSelectionActionsProps) {
  "use no memo";

  const t = useTranslations("proDriver.table");
  const { open: openEmailDialog } = useDialog(EmailProDriverDialog);
  const selected = table.getSelectedRowModel().rows;

  const handleCopyEmails = useCallback(() => {
    const emails = selected.map((s) => s.original.email);
    const emailString = emails.join("; ");
    navigator.clipboard.writeText(emailString);
    toast.success(t("emailsCopied"));
  }, [selected, t]);

  const handleDeselect = useCallback(() => {
    table.toggleAllPageRowsSelected(false);
  }, [table]);

  const handleEmail = useCallback(() => {
    openEmailDialog({
      driverIds: selected.map((s) => s.original.id),
    });
  }, [openEmailDialog, selected]);

  if (selected.length === 0) {
    return null;
  }

  return (
    <div className="flex h-[36px] items-center space-x-2">
      <div className="text-xs text-gray-500">
        {selected.length} {t("selected")}
      </div>

      <Button
        plain
        type="button"
        className="text-xs text-gray-500 underline decoration-dashed"
        onClick={handleDeselect}
      >
        {t("deselect")}
      </Button>

      <Button outline type="button" onClick={handleCopyEmails}>
        <ClipboardDocumentListIcon />
        {t("copyEmails")}
      </Button>

      <Button outline type="button" onClick={handleEmail}>
        <EnvelopeIcon /> {t("email")}
      </Button>
    </div>
  );
}
